import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Search, Plus, X, GripVertical } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { DEFAULT_KEYWORD_CATEGORIES } from '../data/mockData';
import { Checkbox } from './ui/checkbox';

const AddComboModal = ({ isOpen, onClose, onAddCombo, editingCombo }) => {
  const { toast } = useToast();
  const { user, combos, folders } = useAuth();
  
  // Utiliser les variations personnalisées de l'utilisateur ou les valeurs par défaut
  const userVariations = user?.customVariations && Object.keys(user.customVariations).length > 0
    ? user.customVariations
    : DEFAULT_KEYWORD_CATEGORIES;
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    videoLink: '',
    targetFolder: '',
    targetSubfolder: '',
    moves: [],
    customVariations: [],
    status: 'draft',
    isSignature: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMoves, setSelectedMoves] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [selectedMoveForVariation, setSelectedMoveForVariation] = useState(null);
  const [showVariationModal, setShowVariationModal] = useState(false);

  // Ajout d'un hook pour détecter le thème
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.body.classList.contains('light') ? 'light' : 'dark';
    }
    return 'dark';
  });
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.body.classList.contains('light') ? 'light' : 'dark');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Initialize form when editing
  useEffect(() => {
    if (editingCombo) {
      setFormData({
        name: editingCombo.name,
        description: editingCombo.description,
        videoLink: editingCombo.videoLink || '',
        targetFolder: editingCombo.targetFolder || 'none',
        targetSubfolder: editingCombo.targetSubfolder || 'none',
        moves: [],
        customVariations: editingCombo.customVariations || [],
        status: editingCombo.status || 'draft',
        isSignature: !!editingCombo.isSignature
      });
      // Ajouter itemType aux moves existants
      const movesWithType = (editingCombo.moves || []).map(move => ({
        ...move,
        itemType: move.itemType || (move.moves ? 'combo' : 'move')
      }));
      setSelectedMoves(movesWithType);
    } else {
      setFormData({
        name: '',
        description: '',
        videoLink: '',
        targetFolder: 'none',
        targetSubfolder: 'none',
        moves: [],
        customVariations: [],
        status: 'draft',
        isSignature: false
      });
      setSelectedMoves([]);
    }
  }, [editingCombo]);

  const availableMoves = (user?.moves || []).map(m => ({ ...m, isSignature: !!m.isSignature }));
  const availableCombos = combos || [];
  
  // Combiner moves et combos pour la sélection
  const allAvailableItems = [
    ...availableMoves.map(item => ({ ...item, itemType: 'move' })),
    ...availableCombos.map(item => ({ ...item, itemType: 'combo' }))
  ];
  
  const filteredItems = allAvailableItems.filter(item => {
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    if (q === 'signature' || q === 'sign') return !!item.isSignature && item.itemType === 'move';
    return (
      item.name.toLowerCase().includes(q) ||
      (item.type && item.type.toLowerCase().includes(q)) ||
      (item.itemType === 'combo' && 'combo'.includes(q))
    );
  });

  const handleAddItemToCombo = (item) => {
    const newItem = {
      ...item,
      uniqueId: `${item.id}-${Date.now()}-${Math.random()}`,
      itemType: item.itemType || (item.moves ? 'combo' : 'move') // Préserver le type
    };
    setSelectedMoves([...selectedMoves, newItem]);
  };



  const handleAddVariationToMove = (uniqueId, variation) => {
    setSelectedMoves(prev => prev.map(move => {
      if (move.uniqueId === uniqueId) {
        const updatedMove = {
          ...move,
          variations: [...(move.variations || []), variation]
        };
        // Mettre à jour selectedMoveForVariation pour refléter les changements
        if (selectedMoveForVariation?.uniqueId === uniqueId) {
          setSelectedMoveForVariation(updatedMove);
        }
        return updatedMove;
      }
      return move;
    }));
  };

  const handleRemoveVariationFromMove = (uniqueId, variationIndex) => {
    setSelectedMoves(prev => prev.map(move => {
      if (move.uniqueId === uniqueId) {
        const updatedMove = {
          ...move,
          variations: move.variations.filter((_, index) => index !== variationIndex)
        };
        // Mettre à jour selectedMoveForVariation pour refléter les changements
        if (selectedMoveForVariation?.uniqueId === uniqueId) {
          setSelectedMoveForVariation(updatedMove);
        }
        return updatedMove;
      }
      return move;
    }));
  };

  const handleRemoveMoveFromCombo = (uniqueId) => {
    setSelectedMoves(prev => prev.filter(move => move.uniqueId !== uniqueId));
  };

  const handleOpenVariationModal = (move) => {
    setSelectedMoveForVariation(move);
    setShowVariationModal(true);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const newMoves = [...selectedMoves];
      const [draggedMove] = newMoves.splice(draggedIndex, 1);
      newMoves.splice(dropIndex, 0, draggedMove);
      setSelectedMoves(newMoves);
    }
    setDraggedIndex(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || selectedMoves.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please fill in the combo name and add at least one move.",
        variant: "destructive"
      });
      return;
    }

    const newCombo = {
      id: editingCombo ? editingCombo.id : Date.now().toString(),
      name: formData.name,
      description: formData.description,
      videoLink: formData.videoLink || null,
      targetFolder: formData.targetFolder === 'none' ? null : formData.targetFolder,
      targetSubfolder: formData.targetSubfolder === 'none' ? null : formData.targetSubfolder,
      moves: selectedMoves,
      customVariations: formData.customVariations,
      type: 'combo',
      createdAt: editingCombo ? editingCombo.createdAt : new Date().toISOString(),
      status: formData.status || 'draft',
      isSignature: !!formData.isSignature
    };
    
    onAddCombo(newCombo);
    
    toast({
      title: editingCombo ? "Combo Updated! 🎉" : "Combo Added! 🎉",
      description: `${formData.name} has been ${editingCombo ? 'updated' : 'added'} to your arsenal.`
    });

    // Reset form
    setFormData({
      name: '',
      description: '',
      videoLink: '',
      targetFolder: 'none',
      targetSubfolder: 'none',
      moves: [],
      customVariations: [],
      status: 'draft',
      isSignature: false
    });
    setSelectedMoves([]);
    setSearchTerm('');
    
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getTypeColor = (type) => {
    const colors = {
      toprock: 'bg-blue-100 text-blue-800 border-blue-200',
      footwork: 'bg-green-100 text-green-800 border-green-200',
      powermoves: 'bg-red-100 text-red-800 border-red-200',
      freezes: 'bg-purple-100 text-purple-800 border-purple-200',
      transitions: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`sm:max-w-2xl max-h-[90vh] overflow-y-auto border-2 bg-white text-black`}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {editingCombo ? 'Edit Combo' : 'Add New Combo'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Combo Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Toprock to Footwork Combo"
              className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the combo flow and style..."
              rows={3}
              className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200 resize-none`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="videoLink">Video Link</Label>
            <Input
              id="videoLink"
              value={formData.videoLink}
              onChange={(e) => handleChange('videoLink', e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="targetFolder">Target Folder</Label>
              <Select value={formData.targetFolder} onValueChange={(value) => handleChange('targetFolder', value)}>
                <SelectTrigger className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}>
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No folder</SelectItem>
                  {folders && Object.entries(folders).map(([folderKey, folder]) => (
                    <SelectItem key={folderKey} value={folderKey}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="targetSubfolder">Target Subfolder</Label>
              <Select 
                value={formData.targetSubfolder} 
                onValueChange={(value) => handleChange('targetSubfolder', value)}
                disabled={!formData.targetFolder || formData.targetFolder === 'none'}
              >
                <SelectTrigger className={`bg-white text-black border-black focus:ring-2 focus:ring-blue-500 transition-all duration-200`}>
                  <SelectValue placeholder="Select a subfolder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No subfolder</SelectItem>
                  {formData.targetFolder && folders && folders[formData.targetFolder] && 
                    Object.entries(folders[formData.targetFolder].children).map(([subfolderKey, subfolder]) => (
                      <SelectItem key={subfolderKey} value={subfolderKey}>
                        {subfolder.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Checkbox id="isSignature" checked={!!formData.isSignature} onCheckedChange={v => handleChange('isSignature', !!v)} />
              <Label htmlFor="isSignature">Signature</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={v => handleChange('status', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">draft</SelectItem>
                  <SelectItem value="published">published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Selected Moves with Drag & Drop */}
          {selectedMoves.length > 0 && (
            <div className="space-y-2">
              <Label>Selected Moves ({selectedMoves.length}) - Drag to reorder</Label>
              <div className="space-y-2">
                {selectedMoves.map((item, index) => (
                  <div
                    key={`${item.id}-${index}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-move transition-all duration-200 ${
                      draggedIndex === index 
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' 
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">{index + 1}.</span>
                    <span className={`text-sm font-medium text-black`}>{item.name}</span>
                    {item.itemType === 'combo' ? (
                      <div className="flex gap-1">
                        <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-xs">Combo</Badge>
                        <Badge className="bg-gray-100 text-gray-800 border-gray-200 text-xs">{item.moves?.length || 0} moves</Badge>
                      </div>
                    ) : (
                      <Badge className={`text-xs ${getTypeColor(item.type)}`}>
                        {item.type}
                      </Badge>
                    )}
                    {item.variations && item.variations.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {item.variations.map((variation, vIndex) => (
                          <Badge key={vIndex} className="bg-green-100 text-green-800 border-green-200 text-xs">
                            {variation}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleRemoveVariationFromMove(item.uniqueId, vIndex); }}
                              className="ml-1 text-green-600 hover:text-green-800"
                            >
                              <X className="w-2 h-2" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    {item.itemType === 'move' && (
                      <button
                        type="button"
                        onClick={() => handleOpenVariationModal(item)}
                        className="text-blue-500 hover:text-blue-700"
                        title="Add variation"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveMoveFromCombo(item.uniqueId)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}



          {/* Item Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Add Moves and Combos</Label>
              <div className="text-xs text-gray-500">Tip: type "signature" to filter signature moves</div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search moves and combos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              />
            </div>
            
                        <div className="max-h-48 overflow-y-auto border rounded-lg p-2">
              {filteredItems.length > 0 ? (
                <div className="space-y-2">
                  {filteredItems.map((item) => {
                    const isSelected = selectedMoves.find(m => m.id === item.id);
                    return (
                      <Card
                        key={item.id}
                        className={`cursor-pointer transition-all duration-200 ${
                          isSelected 
                            ? 'bg-blue-100 border-blue-300 dark:bg-blue-900 dark:border-blue-700' 
                            : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={() => handleAddItemToCombo(item)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium text-black`}>{item.name}</span>
                              {item.itemType === 'combo' ? (
                                <div className="flex gap-1">
                                  <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-xs">Combo</Badge>
                                  <Badge className="bg-gray-100 text-gray-800 border-gray-200 text-xs">{item.moves?.length || 0} moves</Badge>
                                </div>
                              ) : (
                                <Badge className={`text-xs ${getTypeColor(item.type)}`}>
                                  {item.type}
                                </Badge>
                              )}
                            </div>
                            {isSelected && (
                              <Plus className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No items found</p>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 hover:bg-gray-50 transition-all duration-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200"
            >
              {editingCombo ? 'Update Combo' : 'Add Combo'}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Variation Selection Modal */}
      <Dialog open={showVariationModal} onOpenChange={setShowVariationModal}>
        <DialogContent className={`sm:max-w-md border-2 bg-white text-black`}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              Add Variations to {selectedMoveForVariation?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select variations from your configured categories:
            </p>
            <div className="space-y-3">
              {Object.entries(userVariations).map(([category, variations]) => (
                <div key={category} className="space-y-2">
                  <h4 className="font-medium text-sm capitalize">{category}</h4>
                  <div className="flex flex-wrap gap-2">
                                         {variations.map((variation) => {
                       const isSelected = selectedMoveForVariation?.variations?.includes(variation);
                       return (
                         <button
                           key={variation}
                           type="button"
                           onClick={() => {
                             if (isSelected) {
                               const index = selectedMoveForVariation.variations.indexOf(variation);
                               handleRemoveVariationFromMove(selectedMoveForVariation.uniqueId, index);
                             } else {
                               handleAddVariationToMove(selectedMoveForVariation.uniqueId, variation);
                             }
                           }}
                           className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 ${
                             isSelected
                               ? 'bg-green-600 text-white'
                               : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                           }`}
                         >
                           {variation}
                         </button>
                       );
                     })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowVariationModal(false)}
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AddComboModal; 